import React from 'react';
import { DRAWER_HEADER_HEIGHT, SCREEN_PADDING } from '@/styles';
import {
  LayoutSize,
  useLayoutContext,
  useLayoutSizeValue,
} from './layout-context';
import { Platform, StyleProp, StyleSheet, ViewStyle } from 'react-native';
import { Position, ToastContextProvider } from './toast-context';
import { useGetActiveTabRouteIndex } from '@/hooks/use-get-active-tab-route-index';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

interface Props {
  children: React.ReactNode;
}

const TAB_BAR_HEIGHT = 60;

export const AppTabsToastContextProvider: React.FC<Props> = ({ children }) => {
  const activeTabRouteIndex = useGetActiveTabRouteIndex();
  const { bottom } = useSafeAreaInsets();
  const { isSmallLayout } = useLayoutContext();

  // Always show toast on bottom for native
  const position = Platform.OS === 'web' ? Position.TOP : Position.BOTTOM;

  // Tab bar is only visible for the root route on small screens
  const tabBarAdjustment =
    isSmallLayout && activeTabRouteIndex === 0 ? TAB_BAR_HEIGHT : 0;

  // On large screens, nav header is rendered above the AppTabsContainerStack
  // So all errors thrown by the stack will be shown below the nav header
  // needing no adjustment
  // On tablet, nav header is rendered below the AppTabsContainerStack. So
  // adjust the top position by header height to remain consistent
  const webStyles = useLayoutSizeValue<StyleProp<ViewStyle>>(
    {
      [LayoutSize.SMALL]: styles.toastContainerSmall,
      [LayoutSize.SMALL_TABLET]: styles.toastContainerTablet,
      [LayoutSize.MEDIUM]: styles.toastContainerTablet,
      [LayoutSize.LARGE]: styles.toastContainerLarge,
    },
    true,
  );

  return (
    <ToastContextProvider
      containerStyle={
        position === Position.BOTTOM
          ? { bottom: tabBarAdjustment + SCREEN_PADDING + bottom }
          : webStyles
      }
      position={position}
    >
      {children}
    </ToastContextProvider>
  );
};

const styles = StyleSheet.create({
  toastContainerLarge: {
    top: 0,
  },
  toastContainerSmall: {
    top: SCREEN_PADDING,
  },
  toastContainerTablet: {
    top: DRAWER_HEADER_HEIGHT,
  },
});
